import axios from "axios";
import * as types from "./GroupOrdersTypes";

// Separate axios instance for public endpoints (no auth header).
// Uses a request interceptor to read baseURL lazily (it's set in App.js
// after modules are imported, so snapshotting at import time would be undefined).
const publicAxios = axios.create();
publicAxios.interceptors.request.use((config) => {
  if (!config.baseURL) config.baseURL = axios.defaults.baseURL;
  return config;
});

// ---- Admin Actions ----

export const createGroupOrder = (data) => (dispatch) => {
  dispatch({ type: types.CREATE_GROUP_ORDER_REQUEST });
  axios
    .post("/createGroupOrder", data)
    .then((res) => {
      dispatch({ type: types.CREATE_GROUP_ORDER_SUCCESS, payload: res.data });
    })
    .catch((err) => {
      dispatch({
        type: types.CREATE_GROUP_ORDER_FAILURE,
        payload: err.response?.data || { error: err.message },
      });
    });
};

export const createGroupOrderReset = () => ({
  type: types.CREATE_GROUP_ORDER_RESET,
});

export const fetchGroupOrders = () => (dispatch) => {
  dispatch({ type: types.FETCH_GROUP_ORDERS_REQUEST });
  axios
    .post("/fetchGroupOrders")
    .then((res) => {
      dispatch({ type: types.FETCH_GROUP_ORDERS_SUCCESS, payload: res.data });
    })
    .catch((err) => {
      dispatch({
        type: types.FETCH_GROUP_ORDERS_FAILURE,
        payload: err.response?.data || { error: err.message },
      });
    });
};

export const fetchGroupOrderDetails = (groupOrderId) => (dispatch) => {
  dispatch({ type: types.FETCH_GROUP_ORDER_DETAILS_REQUEST });
  axios
    .post("/fetchGroupOrderDetails", { groupOrderId })
    .then((res) => {
      dispatch({
        type: types.FETCH_GROUP_ORDER_DETAILS_SUCCESS,
        payload: res.data,
      });
    })
    .catch((err) => {
      dispatch({
        type: types.FETCH_GROUP_ORDER_DETAILS_FAILURE,
        payload: err.response?.data || { error: err.message },
      });
    });
};

// ---- Public Actions (no auth) ----

export const fetchGroupOrder = (groupOrderId) => (dispatch) => {
  dispatch({ type: types.FETCH_GROUP_ORDER_REQUEST });
  publicAxios
    .post("/fetchGroupOrder", { groupOrderId })
    .then((res) => {
      dispatch({ type: types.FETCH_GROUP_ORDER_SUCCESS, payload: res.data });
    })
    .catch((err) => {
      dispatch({
        type: types.FETCH_GROUP_ORDER_FAILURE,
        payload: err.response?.data || { error: err.message },
      });
    });
};

export const createGroupPayment = (groupOrderId) => (dispatch) => {
  dispatch({ type: types.CREATE_GROUP_PAYMENT_REQUEST });
  publicAxios
    .post("/createGroupPayment", { groupOrderId })
    .then((res) => {
      dispatch({ type: types.CREATE_GROUP_PAYMENT_SUCCESS, payload: res.data });
    })
    .catch((err) => {
      dispatch({
        type: types.CREATE_GROUP_PAYMENT_FAILURE,
        payload: err.response?.data || { error: err.message },
      });
    });
};

export const confirmGroupPayment =
  (groupOrderId, paymentIntentId) => (dispatch) => {
    dispatch({ type: types.CONFIRM_GROUP_PAYMENT_REQUEST });
    publicAxios
      .post("/confirmGroupPayment", { groupOrderId, paymentIntentId })
      .then((res) => {
        dispatch({
          type: types.CONFIRM_GROUP_PAYMENT_SUCCESS,
          payload: res.data,
        });
      })
      .catch((err) => {
        dispatch({
          type: types.CONFIRM_GROUP_PAYMENT_FAILURE,
          payload: err.response?.data || { error: err.message },
        });
      });
  };

// ---- Coordinator Actions ----

export const fetchMyGroupOrders = () => (dispatch) => {
  dispatch({ type: types.FETCH_MY_GROUP_ORDERS_REQUEST });
  axios
    .post("/fetchMyGroupOrders")
    .then((res) => {
      dispatch({
        type: types.FETCH_MY_GROUP_ORDERS_SUCCESS,
        payload: res.data,
      });
    })
    .catch((err) => {
      dispatch({
        type: types.FETCH_MY_GROUP_ORDERS_FAILURE,
        payload: err.response?.data || { error: err.message },
      });
    });
};

// ---- Student Actions ----

export const redeemGroupCode = (code) => (dispatch) => {
  dispatch({ type: types.REDEEM_GROUP_CODE_REQUEST });
  axios
    .post("/redeemGroupCode", { code })
    .then((res) => {
      dispatch({ type: types.REDEEM_GROUP_CODE_SUCCESS, payload: res.data });
    })
    .catch((err) => {
      dispatch({
        type: types.REDEEM_GROUP_CODE_FAILURE,
        payload: err.response?.data || { error: err.message },
      });
    });
};

export const redeemGroupCodeReset = () => ({
  type: types.REDEEM_GROUP_CODE_RESET,
});

// ---- Public Validate ----

export const validateGroupCode = (code) => (dispatch) => {
  dispatch({ type: types.VALIDATE_GROUP_CODE_REQUEST });
  publicAxios
    .post("/validateGroupCode", { code })
    .then((res) => {
      dispatch({ type: types.VALIDATE_GROUP_CODE_SUCCESS, payload: res.data });
    })
    .catch((err) => {
      dispatch({
        type: types.VALIDATE_GROUP_CODE_FAILURE,
        payload: err.response?.data || { error: err.message },
      });
    });
};

export const validateGroupCodeReset = () => ({
  type: types.VALIDATE_GROUP_CODE_RESET,
});

// ---- Individual Code Payment Actions ----

export const createIndividualCodePayment = (code, paymentMethodId) => (dispatch) => {
  dispatch({ type: types.CREATE_INDIVIDUAL_CODE_PAYMENT_REQUEST });
  return axios
    .post("/createIndividualCodePayment", { code, paymentMethodId })
    .then((res) => {
      dispatch({ type: types.CREATE_INDIVIDUAL_CODE_PAYMENT_SUCCESS, payload: res.data });
      return res.data;
    })
    .catch((err) => {
      const payload = err.response?.data || { error: err.message };
      dispatch({ type: types.CREATE_INDIVIDUAL_CODE_PAYMENT_FAILURE, payload });
      throw err;
    });
};

export const createIndividualCodePaymentReset = () => ({
  type: types.CREATE_INDIVIDUAL_CODE_PAYMENT_RESET,
});

export const confirmIndividualCodePayment = (code, paymentIntentId) => (dispatch) => {
  dispatch({ type: types.CONFIRM_INDIVIDUAL_CODE_PAYMENT_REQUEST });
  return axios
    .post("/confirmIndividualCodePayment", { code, paymentIntentId })
    .then((res) => {
      dispatch({ type: types.CONFIRM_INDIVIDUAL_CODE_PAYMENT_SUCCESS, payload: res.data });
      return res.data;
    })
    .catch((err) => {
      const payload = err.response?.data || { error: err.message };
      dispatch({ type: types.CONFIRM_INDIVIDUAL_CODE_PAYMENT_FAILURE, payload });
      throw err;
    });
};

export const confirmIndividualCodePaymentReset = () => ({
  type: types.CONFIRM_INDIVIDUAL_CODE_PAYMENT_RESET,
});

export const createIndividualCodePaymentPublic = (code, email, name) => (dispatch) => {
  dispatch({ type: types.CREATE_INDIVIDUAL_CODE_PAYMENT_PUBLIC_REQUEST });
  return publicAxios
    .post("/createIndividualCodePaymentPublic", { code, email, name })
    .then((res) => {
      dispatch({ type: types.CREATE_INDIVIDUAL_CODE_PAYMENT_PUBLIC_SUCCESS, payload: res.data });
      return res.data;
    })
    .catch((err) => {
      const payload = err.response?.data || { error: err.message };
      dispatch({ type: types.CREATE_INDIVIDUAL_CODE_PAYMENT_PUBLIC_FAILURE, payload });
      throw err;
    });
};

export const createIndividualCodePaymentPublicReset = () => ({
  type: types.CREATE_INDIVIDUAL_CODE_PAYMENT_PUBLIC_RESET,
});

export const activateGroupOrderPaidCodes = (groupOrderId, autoActivate = true) => (dispatch) => {
  dispatch({ type: types.ACTIVATE_PAID_CODES_REQUEST });
  return axios
    .post("/activateGroupOrderPaidCodes", { groupOrderId, autoActivate })
    .then((res) => {
      dispatch({ type: types.ACTIVATE_PAID_CODES_SUCCESS, payload: res.data });
      return res.data;
    })
    .catch((err) => {
      const payload = err.response?.data || { error: err.message };
      dispatch({ type: types.ACTIVATE_PAID_CODES_FAILURE, payload });
      throw err;
    });
};

export const activateGroupOrderPaidCodesReset = () => ({
  type: types.ACTIVATE_PAID_CODES_RESET,
});

// ---- Admin: Approve/Reject Group Order Request ----

export const approveGroupOrderRequest = (groupOrderId, data) => (dispatch) => {
  dispatch({ type: types.APPROVE_GROUP_ORDER_REQUEST_REQUEST });
  return axios
    .post("/approveGroupOrderRequest", { groupOrderId, ...data })
    .then((res) => {
      dispatch({ type: types.APPROVE_GROUP_ORDER_REQUEST_SUCCESS, payload: res.data });
      return res.data;
    })
    .catch((err) => {
      const payload = err.response?.data || { error: err.message };
      dispatch({ type: types.APPROVE_GROUP_ORDER_REQUEST_FAILURE, payload });
      throw err;
    });
};

export const approveGroupOrderRequestReset = () => ({
  type: types.APPROVE_GROUP_ORDER_REQUEST_RESET,
});

export const rejectGroupOrderRequest = (groupOrderId) => (dispatch) => {
  dispatch({ type: types.REJECT_GROUP_ORDER_REQUEST_REQUEST });
  return axios
    .post("/rejectGroupOrderRequest", { groupOrderId })
    .then((res) => {
      dispatch({ type: types.REJECT_GROUP_ORDER_REQUEST_SUCCESS, payload: res.data });
      return res.data;
    })
    .catch((err) => {
      const payload = err.response?.data || { error: err.message };
      dispatch({ type: types.REJECT_GROUP_ORDER_REQUEST_FAILURE, payload });
      throw err;
    });
};

export const rejectGroupOrderRequestReset = () => ({
  type: types.REJECT_GROUP_ORDER_REQUEST_RESET,
});
